import classNames from "classnames";
import { mapValues } from "lodash";
import * as React from "react";
import { FC, forwardRef, HTMLAttributes, useEffect } from "react";
import { logError } from "~/common/logging";
import * as styles from "./Icon.less";
import { icons } from "./icons";

export const icon: { [K in keyof typeof icons]: K } = mapValues(icons, (_, key) => key) as any;

export type IconNames = keyof typeof icon;

/*
 *   list of available sizes
 *   default = 16px
 *   small = 12px
 *   large = 24px
 * */
export type IconSize = "default" | "small" | "large";

interface IconProps {
    iconName: IconNames;
    className?: string;
    size?: IconSize;
    color?: string;
}

type Props = IconProps & HTMLAttributes<SVGSVGElement>;
export const Icon = forwardRef<SVGSVGElement, Props>((props, ref) => {
    const { iconName, className, size = "default", title, color, ...rest } = props;
    let Path = icons[iconName] ?? icons.fake;

    useEffect(() => {
        if (!icons[iconName]) {
            logError(new Error(`Icon ${iconName} not found`));
        }
    }, [iconName]);

    return (
        <svg
            data-testid={`icon-${iconName}`}
            role="img"
            ref={ref}
            className={classNames(styles.icon, styles[`size_${size}`], className)}
            viewBox="0 0 16 16"
            fill={color || "currentColor"}
            {...rest}
        >
            <>
                {title && <title>{title}</title>}
                <Path />
            </>
        </svg>
    );
});

type BlankIconProps = HTMLAttributes<HTMLDivElement> & {
    size?: IconSize;
};

/**
 * Wraps and centers an element smaller than icon in a div with icon size.
 */
export const BlankIcon: FC<BlankIconProps> = ({ children, className, size = "default", ...props }) => (
    <div data-testid="icon-blank" className={classNames(styles.blank, styles[`size_${size}`], className)} {...props}>
        {children}
    </div>
);

type ColorIconProps = BlankIconProps & {
    color: string;
    borderRadius?: number;
};

export const ColorIcon: FC<ColorIconProps> = ({
    children,
    className,
    size = "default",
    borderRadius = 4,
    ...props
}) => (
    <div
        data-testid={`icon-color`}
        className={classNames(styles.blank, styles[`size_${size}`], className)}
        style={{ backgroundColor: props.color, borderRadius }}
        {...props}
    >
        {children}
    </div>
);
